// @dynamic-i18n-keys
// Dynamic t() at line 58 (labelI18nKeys[label.type]):
//   deviationSubmit.form.labels.tag.aireference
//   deviationSubmit.form.labels.tag.location
import React, { useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSubmitField } from '../../hooks/useSubmitField';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import TagList from '@wix/da-ds/pkg/TagList';
import ButtonFieldLayout from '../../layouts/common/ButtonFieldLayout';
import { useFieldFormPopup } from '../../modals/FieldFormPopup';
import LabelsFormPopup from '../../modals/LabelsFormPopup';
import { SubmitFormValues } from '../../redux/types';
import { SubmitFormContext } from '../../context';
import { initAdapter, submitAdapter } from './adapters';

import s from './LabelsField.scss';

interface Props {
  className?: string;
}

const LabelsField: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();
  const { pubsub } = useContext(SubmitFormContext)!;

  const {
    id,
    field: { onChange, value: labels = [] },
  } = useSubmitField('labels', {
    initAdapter,
    submitAdapter,
  });

  const onModalSave = useCallback(
    (nextValue: SubmitFormValues['labels']) => {
      const sanitizedValue = (nextValue || []).filter(
        i => i.type.length > 0 && i.name.length > 0
      );
      onChange(sanitizedValue);
    },
    [onChange]
  );

  const modalProps = useFieldFormPopup<SubmitFormValues['labels']>(
    labels,
    onModalSave
  );

  const labelI18nKeys = {
    model: 'deviationSubmit.form.labels.tag.model',
    object: 'deviationSubmit.form.labels.tag.object',
    character: 'deviationSubmit.form.labels.tag.character',
    aireference: 'deviationSubmit.form.labels.tag.aireference',
    location: 'deviationSubmit.form.labels.tag.location',
  };

  const tagList = labels.length > 0 && (
    <TagList
      className={s['labels']}
      tags={labels.map(label => ({
        tagname: t(labelI18nKeys[label.type], { value: label.name }),
        className: s['label-item'],
      }))}
      size="small"
      multiline
      isTruncated
    />
  );

  useEffect(() => {
    pubsub.subscribe('labels', action => {
      if (action === 'toggle') {
        modalProps.toggleModal();
      }
    });

    return () => {
      pubsub.unsubscribe('labels');
    };
  }, [pubsub, modalProps]);

  return (
    <div className={className}>
      <ButtonFieldLayout
        label={t('deviationSubmit.form.labels.label')}
        labelProps={{ id: `${id}-label`, htmlFor: id }}
        bottomSlot={tagList}
      >
        <TextButton
          variant="main"
          size="small"
          onClick={modalProps.toggleModal}
        >
          {labels.length > 0
            ? t('deviationSubmit.form.labels.button.edit')
            : t('deviationSubmit.form.labels.button.add')}
        </TextButton>
      </ButtonFieldLayout>

      <LabelsFormPopup {...modalProps} />
    </div>
  );
};

export default LabelsField;
